import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PlusCircle, FileEdit, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { format, parseISO, addWeeks } from "date-fns";

interface CheckIn {
  check_in_id: string;
  name: string;
  description: string;
  first_setup_completed: boolean;
  slug: string;
  is_active: boolean;
  participants: number;
  lastCheckInDate: string | null;
  nextCheckInDate: string | null;
  participation: number | null;
  status: 'active' | 'paused' | 'draft';
  avatar_url?: string | null;
}

export function CheckIns() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showActive, setShowActive] = useState(true);
  const [showDrafts, setShowDrafts] = useState(true);
  const [showPaused, setShowPaused] = useState(true);

  useEffect(() => {
    const fetchCheckIns = async () => {
      if (!user?.id) return;
      setLoading(true);
      setError(null);
      const { data: checkInsRaw, error } = await supabase
        .from("check_ins")
        .select("check_in_id, name, description, first_setup_completed, slug, is_active")
        .eq("created_by_user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) {
        setError("Failed to load check-ins. Please try again later.");
        setCheckIns([]);
        setLoading(false);
        return;
      }
      const checkInsWithData: CheckIn[] = await Promise.all(
        (checkInsRaw || []).map(async (ci: any) => {
          const { count: participantsCount } = await supabase
            .from("check_in_employees")
            .select("employee_id", { count: "exact", head: true })
            .eq("check_in_id", ci.check_in_id);
          const { data: reports } = await supabase
            .from("reports")
            .select("report_id, report_date")
            .eq("check_in_id", ci.check_in_id)
            .order("report_date", { ascending: false });
          let lastCheckInDate: string | null = null;
          let nextCheckInDate: string | null = null;
          let participation: number | null = null;
          if (reports && reports.length > 0) {
            lastCheckInDate = reports[0].report_date;
            const { count: responsesCount } = await supabase
              .from("check_in_responses")
              .select("response_id", { count: "exact", head: true })
              .eq("report_id", reports[0].report_id);
            participation = participantsCount && participantsCount > 0 ? Math.round(((responsesCount || 0) / participantsCount) * 100) : null;
            nextCheckInDate = lastCheckInDate ? format(addWeeks(parseISO(lastCheckInDate), 1), "yyyy-MM-dd") : null;
          }
          let status: 'active' | 'paused' | 'draft' = 'draft';
          if (ci.first_setup_completed) {
            status = ci.is_active ? 'active' : 'paused';
          }
          return {
            ...ci,
            participants: participantsCount || 0,
            lastCheckInDate,
            nextCheckInDate,
            participation,
            status,
            avatar_url: null,
          };
        })
      );
      setCheckIns(checkInsWithData);
      setLoading(false);
    };
    fetchCheckIns();
  }, [user]);

  const startNewCheckIn = () => {
    navigate("/check-ins/new");
  };

  const handleEditDraft = (slug: string) => {
    navigate(`/check-ins/new?draft=${slug}`);
  };

  const handleViewCompleted = (slug: string) => {
    navigate(`/check-ins/report/${slug}`);
  };

  // Filter by search
  const searchLower = search.toLowerCase();
  const filteredDrafts = showDrafts
    ? checkIns.filter(
        (c) =>
          c.status === 'draft' &&
          ((c.name?.toLowerCase() || "").includes(searchLower) ||
            (c.description?.toLowerCase() || "").includes(searchLower))
      )
    : [];
  const filteredActive = showActive
    ? checkIns.filter(
        (c) =>
          c.status === 'active' &&
          ((c.name?.toLowerCase() || "").includes(searchLower) ||
            (c.description?.toLowerCase() || "").includes(searchLower))
      )
    : [];
  const filteredPaused = showPaused
    ? checkIns.filter(
        (c) =>
          c.status === 'paused' &&
          ((c.name?.toLowerCase() || "").includes(searchLower) ||
            (c.description?.toLowerCase() || "").includes(searchLower))
      )
    : [];

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 sm:gap-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">Check Ins</h1>
        <Button 
          size="sm" 
          className="gap-2 self-start"
          onClick={startNewCheckIn}
        >
          <PlusCircle className="h-4 w-4" />
          Create New Check-In
        </Button>
      </div>
      {/* Search and Filters Row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2 w-full">
        <div className="w-full sm:max-w-md">
          <Input
            placeholder="Search for check-in..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-0"
          />
        </div>
        <div className="flex items-center gap-4 mt-2 sm:mt-0 w-full sm:w-auto justify-start sm:justify-end">
          <span className="text-sm font-medium text-muted-foreground">Filters:</span>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={showActive} onChange={() => setShowActive((v) => !v)} className="accent-primary" />
            <span className="text-sm">Active</span>
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={showDrafts} onChange={() => setShowDrafts((v) => !v)} className="accent-primary" />
            <span className="text-sm">Drafts</span>
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={showPaused} onChange={() => setShowPaused((v) => !v)} className="accent-primary" />
            <span className="text-sm">Paused</span>
          </label>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-[40vh]">
          <span className="text-muted-foreground">Loading check-ins...</span>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-[40vh]">
          <span className="text-destructive">{error}</span>
        </div>
      ) : checkIns.length === 0 ? (
        <div className="flex items-center justify-center h-[40vh] sm:h-[50vh] md:h-[60vh] border rounded-lg border-dashed">
          <div className="text-center max-w-xs sm:max-w-sm md:max-w-md mx-auto p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-semibold mb-2">No check ins yet</h3>
            <p className="text-sm sm:text-base text-muted-foreground mb-4">
              Create your first check in to start tracking time and progress
            </p>
            <Button onClick={startNewCheckIn}>
              Create Check In
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Draft Check-Ins First */}
          {filteredDrafts.map((checkIn) => (
            <Card key={checkIn.check_in_id} className="w-full border-yellow-300 bg-muted/30 shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-6">
                <div className="flex items-center gap-4 w-full md:w-auto">
                  <Avatar style={{ backgroundColor: stringToColor(checkIn.name) }}>
                    {checkIn.avatar_url ? (
                      <AvatarImage src={checkIn.avatar_url} alt={checkIn.name} />
                    ) : (
                      <AvatarFallback>{getInitials(checkIn.name)}</AvatarFallback>
                    )}
                  </Avatar>
                  <div>
                    <div className="font-semibold text-lg truncate flex items-center gap-2">
                      {checkIn.name || <span className="italic text-muted-foreground">Untitled Draft</span>}
                      <Badge variant="outline" className="ml-2 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 border-purple-200">DRAFT</Badge>
                    </div>
                    <div className="text-muted-foreground text-sm truncate max-w-xs">{checkIn.description || <span className="italic text-muted-foreground">No description</span>}</div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center w-full md:w-auto">
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">Last Check-In</span>
                    <span className="font-medium">-</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">Next Check-In</span>
                    <span className="font-medium">-</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-xs text-muted-foreground">Participation</span>
                    <Badge className="mt-1" variant="secondary">0%</Badge>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleEditDraft(checkIn.slug)}>
                    <FileEdit className="h-4 w-4 mr-2" /> Continue Editing
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          {/* Active Check-Ins */}
          {filteredActive.map((checkIn) => (
            <Card key={checkIn.check_in_id} className="w-full shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-6">
                <div className="flex items-center gap-4 w-full md:w-auto">
                  <Avatar style={{ backgroundColor: stringToColor(checkIn.name) }}>
                    {checkIn.avatar_url ? (
                      <AvatarImage src={checkIn.avatar_url} alt={checkIn.name} />
                    ) : (
                      <AvatarFallback>{getInitials(checkIn.name)}</AvatarFallback>
                    )}
                  </Avatar>
                  <div>
                    <div className="font-semibold text-lg truncate flex items-center gap-2">
                      {checkIn.name}
                      <Badge className="ml-2 text-xs px-2 py-0.5 bg-green-100 text-green-700 border-green-200">ACTIVE</Badge>
                    </div>
                    <div className="text-muted-foreground text-sm truncate max-w-xs">{checkIn.description}</div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center w-full md:w-auto">
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">Last Check-In</span>
                    <span className="font-medium">{checkIn.lastCheckInDate ? format(parseISO(checkIn.lastCheckInDate), 'yyyy-MM-dd') : '-'}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">Next Check-In</span>
                    <span className="font-medium">{checkIn.nextCheckInDate ? checkIn.nextCheckInDate : '-'}</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-xs text-muted-foreground">Participation</span>
                    <Badge className="mt-1" variant="secondary">{checkIn.participation !== null ? `${checkIn.participation}%` : '-'}</Badge>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => handleViewCompleted(checkIn.slug)}>
                    <CheckCircle2 className="h-4 w-4 mr-2" /> View Reports
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          {/* Paused Check-Ins */}
          {filteredPaused.map((checkIn) => (
            <Card key={checkIn.check_in_id} className="w-full shadow-sm hover:shadow-lg transition-shadow border-blue-200 bg-blue-50/40">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-6">
                <div className="flex items-center gap-4 w-full md:w-auto">
                  <Avatar style={{ backgroundColor: stringToColor(checkIn.name) }}>
                    {checkIn.avatar_url ? (
                      <AvatarImage src={checkIn.avatar_url} alt={checkIn.name} />
                    ) : (
                      <AvatarFallback>{getInitials(checkIn.name)}</AvatarFallback>
                    )}
                  </Avatar>
                  <div>
                    <div className="font-semibold text-lg truncate flex items-center gap-2">
                      {checkIn.name}
                      <Badge className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 border-blue-200">PAUSED</Badge>
                    </div>
                    <div className="text-muted-foreground text-sm truncate max-w-xs">{checkIn.description}</div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center w-full md:w-auto">
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">Last Check-In</span>
                    <span className="font-medium">{checkIn.lastCheckInDate ? format(parseISO(checkIn.lastCheckInDate), 'yyyy-MM-dd') : '-'}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">Next Check-In</span>
                    <span className="font-medium">{checkIn.nextCheckInDate ? checkIn.nextCheckInDate : '-'}</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-xs text-muted-foreground">Participation</span>
                    <Badge className="mt-1" variant="secondary">{checkIn.participation !== null ? `${checkIn.participation}%` : '-'}</Badge>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => handleViewCompleted(checkIn.slug)}>
                    <CheckCircle2 className="h-4 w-4 mr-2" /> View Reports
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          {/* No results for search */}
          {filteredActive.length === 0 && filteredDrafts.length === 0 && filteredPaused.length === 0 && (
            <div className="flex items-center justify-center h-[30vh]">
              <span className="text-muted-foreground">No check-ins match your search.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper: get initials from name
function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
// Helper: string to color (for avatar background)
function stringToColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ('00' + value.toString(16)).slice(-2);
  }
  return color + '22'; // add some transparency
}